import React, { useState } from 'react';
import { Modal, Button, Input, Toast, Typography, Space } from '@douyinfe/semi-ui';
import { IconSend, IconTick } from '@douyinfe/semi-icons';
import { useNoonLoginStore } from '@/store/noonLogin';
import useUserStore from '@/store/user';
import request from '@/utils/request';

const { Text } = Typography;

const NoonLoginModal: React.FC = () => {
  const { visible, message, retryConfig, step, maskedEmail, loading, hide, setStep, setMaskedEmail, setLoading } =
    useNoonLoginStore();
  const { userInfo } = useUserStore();
  const [otpCode, setOtpCode] = useState('');

  const handleSendOtp = async () => {
    if (!userInfo?.id || !userInfo?.country) {
      Toast.error('用户信息缺失');
      return;
    }
    setLoading(true);
    setStep('sending');
    try {
      const res = await request.post('/store-authorization/otp_send', {
        cid: userInfo.id,
        country: userInfo.country.split(',')[0],
      });
      const data = res.data?.data || res.data;
      if (!data?.success) {
        Toast.error(data?.message || '发送验证码失败');
        setStep('idle');
        return;
      }
      if (data?.auto === true) {
        Toast.success('自动登录成功');
        hide();
        retryOriginalRequest();
      } else {
        setMaskedEmail(data?.email || '');
        setStep('otpInput');
      }
    } catch (e: any) {
      Toast.error(e?.msg || '发送验证码失败');
      setStep('idle');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpCode || otpCode.length < 6) {
      Toast.error('请输入6位验证码');
      return;
    }
    if (!userInfo?.id || !userInfo?.country) return;
    setLoading(true);
    setStep('verifying');
    try {
      await request.post('/store-authorization/otp_verify', {
        cid: userInfo.id,
        country: userInfo.country.split(',')[0],
        otp_code: otpCode,
      });
      Toast.success('登录成功');
      hide();
      setOtpCode('');
      retryOriginalRequest();
    } catch (e: any) {
      Toast.error(e?.msg || '验证码错误');
      setStep('otpInput');
    } finally {
      setLoading(false);
    }
  };

  const retryOriginalRequest = () => {
    if (retryConfig) {
      request(retryConfig).catch(() => {});
    }
  };

  const handleCancel = () => {
    hide();
    setOtpCode('');
  };

  return (
    <Modal
      title='Noon 登录授权'
      visible={visible}
      onCancel={handleCancel}
      footer={null}
      width={420}
      maskClosable={false}
      closable={!loading}
    >
      <Space vertical align='start' className='w-full' spacing='medium'>
        <Text type='warning'>{message}</Text>

        {step === 'idle' && (
          <Button
            type='primary'
            icon={<IconSend />}
            onClick={handleSendOtp}
            loading={loading}
            block
          >
            发送验证码
          </Button>
        )}

        {step === 'sending' && (
          <Button type='primary' loading block>
            正在发送...
          </Button>
        )}

        {step === 'otpInput' && (
          <>
            {maskedEmail && (
              <Text type='tertiary' size='small'>
                验证码已发送至：{maskedEmail}
              </Text>
            )}
            <Input
              value={otpCode}
              onChange={setOtpCode}
              placeholder='请输入验证码'
              maxLength={6}
              size='large'
              style={{ letterSpacing: 8, textAlign: 'center', fontSize: 20 }}
            />
            <Button
              type='primary'
              icon={<IconTick />}
              onClick={handleVerifyOtp}
              loading={loading}
              block
            >
              验证登录
            </Button>
          </>
        )}

        {step === 'verifying' && (
          <Button type='primary' loading block>
            验证中...
          </Button>
        )}
      </Space>
    </Modal>
  );
};

export default NoonLoginModal;
